import { useState, useMemo, useEffect } from 'react';
import {
  FileText,
  Search,
  Upload,
  RefreshCw,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Database,
  Lock,
  Users,
  Globe,
  Loader2,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDate } from '@/utils/dateFormatter';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import { DocumentListSkeleton } from './KnowledgeBaseSkeletons';
import { DocumentViewerModal } from './DocumentViewerModal';
import { knowledgeBaseApi } from '@/lib/api';
import { toast } from 'sonner';
import type { KbCollection, KbDocument } from '@/types/knowledge-base';

interface DocumentListViewProps {
  collection: KbCollection;
  documents: KbDocument[];
  loading: boolean;
  rebuildingIndex: boolean;
  onBack: () => void;
  onUploadDocument: () => void;
  onRebuildIndex: () => void;
  onDeleteDocument: (document: KbDocument) => void;
  onDocumentsChange?: () => void;
}

export function DocumentListView({
  collection,
  documents,
  loading,
  rebuildingIndex,
  onBack,
  onUploadDocument,
  onRebuildIndex,
  onDeleteDocument,
  onDocumentsChange,
}: DocumentListViewProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useViewportPageSize({
    rowHeight: 62,
    minPageSize: 5,
    maxPageSize: 50,
    bufferSpace: 5,
    loading,            // Recalculate when loading completes
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'name' | 'status' | 'chunks' | 'size'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Per-document indexing state
  const [indexingDocuments, setIndexingDocuments] = useState<Set<string>>(new Set());

  // Document viewer modal state
  const [viewingDocument, setViewingDocument] = useState<KbDocument | null>(null);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    const filtered = documents.filter((doc) => {
      const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });

    // Sort the filtered results
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'chunks':
          comparison = a.chunkCount - b.chunkCount;
          break;
        case 'size':
          comparison = (a.fileSizeBytes || 0) - (b.fileSizeBytes || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [documents, searchQuery, sortColumn, sortDirection]);

  // Paginated documents
  const paginatedDocuments = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredDocuments.slice(startIndex, endIndex);
  }, [filteredDocuments, currentPage, pageSize]);

  // Total pages
  const totalPages = Math.ceil(filteredDocuments.length / pageSize);

  // Handle column sort
  const handleSort = (column: 'name' | 'status' | 'chunks' | 'size') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Handle per-document re-index
  const handleReindexDocument = async (doc: KbDocument) => {
    try {
      setIndexingDocuments((prev) => new Set(prev).add(doc.id));
      await knowledgeBaseApi.reindexDocument(collection.id, doc.id);
      toast.success(`Re-indexing "${doc.name}"...`);
      // Poll for completion or trigger refresh
      onDocumentsChange?.();
    } catch (error: any) {
      console.error('Failed to re-index document:', error);
      toast.error('Failed to re-index document');
    } finally {
      setIndexingDocuments((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return <Globe className="w-3 h-3" />;
      case 'team':
        return <Users className="w-3 h-3" />;
      default:
        return <Lock className="w-3 h-3" />;
    }
  };

  const formatBytes = (bytes: number | undefined | null) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return '-';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusVariant = (status: string | undefined) => {
    switch (status) {
      case 'ready':
        return 'default';
      case 'processing':
      case 'indexing':
        return 'secondary';
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const isDocumentIndexing = (doc: KbDocument) => {
    return (
      indexingDocuments.has(doc.id) ||
      doc.status === 'processing' ||
      doc.status === 'indexing'
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header - Compact single row layout */}
      <div className="flex-none mb-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left side: Back button + Collection info */}
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="flex-shrink-0">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <Database className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <h2 className="text-lg font-semibold truncate">{collection.name}</h2>
              <Badge variant="outline" className="text-xs flex items-center gap-1 flex-shrink-0">
                {getVisibilityIcon(collection.visibility)}
                {collection.visibility}
              </Badge>
              <span className="text-muted-foreground mx-1 flex-shrink-0">•</span>
              {collection.description && (
                <>
                  <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                    {collection.description}
                  </span>
                  <span className="text-muted-foreground mx-1 flex-shrink-0">•</span>
                </>
              )}
              <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                {collection.embeddingModel} ({collection.embeddingDimensions}d) • {collection.documentCount} docs • {collection.chunkCount} chunks
              </span>
            </div>
          </div>

          {/* Right side: Search + Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRebuildIndex}
                    disabled={rebuildingIndex || documents.length === 0}
                  >
                    {rebuildingIndex ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    Rebuild All
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Re-index all documents in this collection</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button size="sm" onClick={onUploadDocument}>
              <Upload className="w-4 h-4 mr-1" />
              Upload Document
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex-1 overflow-auto">
          <DocumentListSkeleton />
        </div>
      ) : filteredDocuments.length === 0 ? (
        /* Empty State */
        <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-muted p-3 mb-4">
            <FileText className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">
            {searchQuery ? 'No documents found' : 'No documents yet'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery
              ? 'Try adjusting your search query'
              : 'Upload documents to this collection to enable semantic search'}
          </p>
          {!searchQuery && (
            <Button onClick={onUploadDocument}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Table - Scrollable */}
          <div className="flex-1 overflow-auto">
            <div className="border rounded-lg bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        {sortColumn === 'name' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {sortColumn === 'status' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[10%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('chunks')}
                      >
                        Chunks
                        {sortColumn === 'chunks' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[10%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('size')}
                      >
                        Size
                        {sortColumn === 'size' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">Source</TableHead>
                    <TableHead className="w-[15%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedDocuments.map((doc) => (
                    <TableRow
                      key={doc.id}
                      className={`hover:bg-muted/50 transition-colors ${
                        rebuildingIndex ? 'animate-pulse bg-blue-50/50 dark:bg-blue-950/20' : ''
                      }`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium truncate max-w-[200px]">{doc.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {doc.fileType}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {rebuildingIndex || isDocumentIndexing(doc) ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1.5">
                                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-500" />
                                    <span className="text-xs text-blue-500">
                                      {rebuildingIndex ? 'Rebuilding...' : 'Indexing...'}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {rebuildingIndex
                                      ? 'Collection is being rebuilt'
                                      : 'Document is being indexed'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : doc.status ? (
                            <Badge variant={getStatusVariant(doc.status)} className="text-xs">
                              {doc.status}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{doc.chunkCount}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatBytes(doc.fileSizeBytes)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{doc.sourceType}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* View button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => setViewingDocument(doc)}
                                >
                                  <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>View document</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {/* Re-index button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleReindexDocument(doc)}
                                  disabled={isDocumentIndexing(doc)}
                                >
                                  {isDocumentIndexing(doc) ? (
                                    <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Re-index this document</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {/* Delete button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => onDeleteDocument(doc)}
                                >
                                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Delete this document</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination Footer - Fixed at bottom */}
          {filteredDocuments.length > 0 && (
            <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing {(currentPage - 1) * pageSize + 1} to{' '}
                {Math.min(currentPage * pageSize, filteredDocuments.length)} of{' '}
                {filteredDocuments.length} documents
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium whitespace-nowrap">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        open={viewingDocument !== null}
        onClose={() => setViewingDocument(null)}
        document={viewingDocument}
        collectionId={collection.id}
      />
    </div>
  );
}
